/**
 * ChunkedUploader - Enterprise-grade large file upload utility
 * 
 * Features:
 * - S3 Multipart Upload
 * - Direct S3 chunk upload (bypasses backend)
 * - Progress tracking per chunk
 * - Parallel chunk uploads with concurrency control
 * - Background upload support
 * - Error handling and retry logic
 * - Cancellation support
 */

import api from '../services/api';

const DEFAULT_CHUNK_SIZE = 10 * 1024 * 1024; // 10MB
const DEFAULT_CONCURRENCY = 3; // Upload 3 chunks in parallel
const MAX_RETRIES = 3;
const PART_UPLOAD_TIMEOUT_MS = 5 * 60 * 1000; // 5 min per chunk

export class ChunkedUploader {
    constructor(file, options = {}) {
        this.file = file;
        this.fileName = file.name;
        this.fileSize = file.size;
        this.fileType = file.type;
        
        // Options
        this.folderId = options.folderId || null;
        this.description = options.description || null;
        // Configurable API endpoints (defaults to library paths)
        this.endpoints = options.endpoints || {
            initiate: '/library/upload/initiate',
            complete: '/library/upload/complete',
            abort: '/library/upload/abort'
        };
        // Extra fields merged into initiate / complete request bodies
        this.extraInitiateFields = options.extraInitiateFields || {};
        this.extraCompleteFields = options.extraCompleteFields || {};
        this.chunkSize = options.chunkSize || DEFAULT_CHUNK_SIZE;
        this.concurrency = options.concurrency || DEFAULT_CONCURRENCY;
        
        // Callbacks
        this.onProgress = options.onProgress || (() => {});
        this.onComplete = options.onComplete || (() => {});
        this.onError = options.onError || (() => {});
        this.onStateChange = options.onStateChange || (() => {});
        
        // State
        this.uploadId = null;
        this.objectKey = null;
        this.totalParts = 0;
        this.uploadedParts = [];
        this.failedParts = new Set();
        this.partRetries = new Map();
        this.aborted = false;
        this.paused = false;
        this.state = 'idle'; // idle, initiating, uploading, completing, completed, failed, aborted
        
        // Progress tracking
        this.uploadedBytes = 0;
        this.totalBytes = file.size;
    }
    
    /**
     * Start the upload process
     */
    async start() {
        try {
            this.setState('initiating');
            
            // Step 1: Initiate multipart upload
            const initResponse = await api.post(this.endpoints.initiate, {
                file_name: this.fileName,
                file_size: this.fileSize,
                file_type: this.fileType,
                folder_id: this.folderId,
                ...this.extraInitiateFields
            });
            
            this.uploadId = initResponse.data.upload_id;
            this.objectKey = initResponse.data.object_key;
            this.totalParts = initResponse.data.total_parts;
            this.chunkSize = initResponse.data.chunk_size;
            const presignedUrls = initResponse.data.presigned_urls;
            
            if (this.aborted) {
                await this.abort();
                return;
            }
            
            this.setState('uploading');
            
            // Step 2: Upload all parts in parallel (with concurrency limit)
            await this.uploadParts(presignedUrls);
            
            if (this.aborted) {
                await this.abort();
                return;
            }
            
            if (this.failedParts.size > 0) {
                throw new Error(`Failed to upload ${this.failedParts.size} parts`);
            }
            
            this.setState('completing');
            
            // Step 3: Complete multipart upload
            const completeResponse = await api.post(this.endpoints.complete, {
                upload_id: this.uploadId,
                object_key: this.objectKey,
                parts: this.uploadedParts.map(p => ({
                    part_number: p.partNumber,
                    etag: p.etag
                })),
                file_name: this.fileName,
                file_size: this.fileSize,
                file_type: this.fileType,
                folder_id: this.folderId,
                description: this.description,
                ...this.extraCompleteFields
            });
            
            this.setState('completed');
            this.onComplete(completeResponse.data);
            
        } catch (error) {
            if (this.aborted) {
                return;
            }
            
            this.setState('failed');
            this.onError(error);
            
            // Try to abort the upload on S3
            if (this.uploadId && this.objectKey) {
                try {
                    await this.abort();
                } catch (abortError) {
                    console.error('Failed to abort upload:', abortError);
                }
            }
        }
    }
    
    /**
     * Upload all parts with concurrency control
     */
    async uploadParts(presignedUrls) {
        const queue = [...presignedUrls];
        const inProgress = new Set();
        
        const uploadNext = async () => {
            if (queue.length === 0 || this.aborted) {
                return;
            }
            
            const urlInfo = queue.shift();
            const partNumber = urlInfo.part_number;
            inProgress.add(partNumber);
            
            try {
                await this.uploadPart(partNumber, urlInfo.url);
                inProgress.delete(partNumber);
                
                // Start next upload
                if (queue.length > 0 && !this.aborted) {
                    uploadNext();
                }
            } catch (error) {
                inProgress.delete(partNumber);
                
                // Retry logic
                const retries = this.partRetries.get(partNumber) || 0;
                if (retries < MAX_RETRIES && !this.aborted) {
                    this.partRetries.set(partNumber, retries + 1);
                    queue.push(urlInfo); // Re-add to queue
                    uploadNext(); // Try next
                } else {
                    this.failedParts.add(partNumber);
                }
            }
        };
        
        // Start initial batch
        const initialBatch = Math.min(this.concurrency, presignedUrls.length);
        for (let i = 0; i < initialBatch; i++) {
            uploadNext();
        }
        
        // Wait for all parts to complete
        while (inProgress.size > 0 || queue.length > 0) {
            if (this.aborted) break;
            await new Promise(resolve => setTimeout(resolve, 100));
        }
    }
    
    /**
     * Upload a single part to S3
     */
    async uploadPart(partNumber, presignedUrl) {
        const start = (partNumber - 1) * this.chunkSize;
        const end = Math.min(start + this.chunkSize, this.fileSize);
        const chunk = this.file.slice(start, end);
        
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            
            xhr.upload.addEventListener('progress', (e) => {
                if (e.lengthComputable && !this.aborted) {
                    // Update this part's progress
                    const previousProgress = this.getPartProgress(partNumber);
                    const currentProgress = e.loaded;
                    const delta = currentProgress - previousProgress;
                    
                    this.uploadedBytes += delta;
                    this.setPartProgress(partNumber, currentProgress);
                    
                    // Calculate overall progress
                    const overallProgress = Math.min(
                        Math.round((this.uploadedBytes / this.totalBytes) * 100),
                        99 // Never show 100% until complete
                    );
                    
                    this.onProgress({
                        uploadedBytes: this.uploadedBytes,
                        totalBytes: this.totalBytes,
                        percentage: overallProgress,
                        partNumber,
                        totalParts: this.totalParts
                    });
                }
            });
            
            xhr.addEventListener('load', () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    const etag = xhr.getResponseHeader('ETag');
                    if (!etag) {
                        reject(new Error('No ETag in response'));
                        return;
                    }
                    
                    this.uploadedParts.push({
                        partNumber,
                        etag: etag.replace(/"/g, '') // Remove quotes
                    });
                    
                    resolve();
                } else {
                    reject(new Error(`Upload failed with status ${xhr.status}`));
                }
            });
            
            xhr.addEventListener('error', () => {
                reject(new Error('Network error during upload'));
            });

            xhr.addEventListener('abort', () => {
                reject(new Error('Upload aborted'));
            });

            xhr.addEventListener('timeout', () => {
                reject(new Error(`Part ${partNumber} upload timed out`));
            });

            xhr.open('PUT', presignedUrl);
            xhr.timeout = PART_UPLOAD_TIMEOUT_MS;
            xhr.send(chunk);
            
            // Store xhr for potential cancellation
            this.setPartXHR(partNumber, xhr);
        });
    }
    
    /**
     * Abort the upload
     */
    async abort() {
        this.aborted = true;
        this.setState('aborted');
        
        // Cancel all in-flight XHR requests
        if (this.partXHRs) {
            this.partXHRs.forEach(xhr => {
                try {
                    xhr.abort();
                } catch (e) {
                    // Ignore errors during abort
                }
            });
        }
        
        // Notify S3 to clean up parts
        if (this.uploadId && this.objectKey) {
            try {
                await api.post(this.endpoints.abort, {
                    upload_id: this.uploadId,
                    object_key: this.objectKey
                });
            } catch (error) {
                console.error('Failed to abort multipart upload:', error);
            }
        }
    }
    
    /**
     * Pause the upload (not implemented yet)
     */
    pause() {
        this.paused = true;
        // TODO: Implement pause/resume
    }
    
    /**
     * Resume the upload (not implemented yet)
     */
    resume() {
        this.paused = false;
        // TODO: Implement pause/resume
    }
    
    /**
     * Set upload state
     */
    setState(newState) {
        this.state = newState;
        this.onStateChange(newState);
    }
    
    /**
     * Helper: Store XHR for a part (for cancellation)
     */
    setPartXHR(partNumber, xhr) {
        if (!this.partXHRs) {
            this.partXHRs = new Map();
        }
        this.partXHRs.set(partNumber, xhr);
    }
    
    /**
     * Helper: Get part progress
     */
    getPartProgress(partNumber) {
        if (!this.partProgress) {
            this.partProgress = new Map();
        }
        return this.partProgress.get(partNumber) || 0;
    }
    
    /**
     * Helper: Set part progress
     */
    setPartProgress(partNumber, bytes) {
        if (!this.partProgress) {
            this.partProgress = new Map();
        }
        this.partProgress.set(partNumber, bytes);
    }
}

/**
 * Helper function to determine if a file should use chunked upload
 */
export const shouldUseChunkedUpload = (fileSize) => {
    // Use chunked upload for files >= 50MB
    return fileSize >= 50 * 1024 * 1024;
};
